import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { AssessmentQuestion } from "@/components/AssessmentQuestion";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, ArrowRight, ListChecks, Sparkles, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
/**
 * Assessment page — three internal views:
 *   - "intro":    landing / start-or-resume
 *   - "questions": one question at a time, with autosave
 *   - "dcl":      digital-confidence result at the end of section 6
 *
 * Lane-aware: reads `?lane=quick|complete` from the URL. Falls back to the
 * lane recorded on the server's progress row, which defaults to `complete`
 * for grandfathered users. The Review, Consent, Submitted routes are
 * separate and inherit the lane from state.progress.lane.
 */
export default function Assessment() {
  const [view, setView] = useState("loading");
  const [defs, setDefs] = useState(null);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [cursorIdx, setCursorIdx] = useState(0);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const laneParam = params.get("lane");

  const load = useCallback(async () => {
    try {
      // Load state first so we can decide the lane, then load matching definitions.
      const s = await api.get("/assessment/state" + (laneParam ? `?lane=${laneParam}` : ""));
      const lane = laneParam || s.data.progress?.lane || null;
      const d = await api.get("/assessment/definitions" + (lane ? `?lane=${lane}` : ""));
      setDefs(d.data);
      setState(s.data);
      // resume position
      const qs = d.data.questions;
      let idx = 0;
      const currentQid = s.data.progress?.current_question_id;
      if (currentQid) {
        const j = qs.findIndex((q) => q.id === currentQid);
        if (j >= 0) idx = j;
      }
      setCursorIdx(idx);
      setView("intro");
    } catch (err) {
      setError(extractApiError(err, "Failed to load assessment."));
      setView("intro");
    }
  }, [laneParam]);

  useEffect(() => { load(); }, [load]);

  const retryLoad = useCallback(() => {
    setError(null);
    setView("loading");
    load();
  }, [load]);

  if (view === "loading" || !defs || !state) {
    return (
      <AppShell>
        <div data-testid="assessment-loading" className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading your assessment…
        </div>
      </AppShell>
    );
  }

  // Honest error-state with retry if definitions/state failed to load.
  if (error && (!defs || !state)) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <Alert variant="destructive" data-testid="assessment-load-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-6">
            <Button onClick={retryLoad} data-testid="assessment-load-retry" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" /> Try again
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  if (view === "intro") {
    return <IntroView defs={defs} state={state} error={error} resumeIdx={cursorIdx} onStart={(idx) => { setCursorIdx(idx); setView("questions"); }} />;
  }

  return (
    <QuestionsView
      defs={defs}
      state={state}
      cursorIdx={cursorIdx}
      setCursorIdx={setCursorIdx}
      onStateChange={setState}
      onExit={() => setView("intro")}
      onReview={() => {
        const lane = state?.progress?.lane || laneParam;
        navigate("/assessment/review" + (lane ? `?lane=${lane}` : ""));
      }}
    />
  );
}


// ---------- Intro ----------

function IntroView({ defs, state, error, onStart, resumeIdx = 0 }) {
  const questions = defs.questions;
  const answered = Object.keys(state.answers).filter((qid) => !state.answers[qid].skipped && questions.some((q) => q.id === qid)).length;
  const total = questions.length;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const submitted = !!state.progress?.submitted_at || !!state.progress?.quick_submitted_at;
  const lane = state?.progress?.lane || defs.lane || "complete";
  const laneLabel = lane === "quick" ? "Quick Repath" : "Complete Career Repath Assessment";

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-wider text-fg-secondary" data-testid="assessment-lane-label">
          {laneLabel} · guided discovery assessment
        </p>
        <h1
          data-testid="assessment-title"
          className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
        >
          {defs.title}
        </h1>
        <p className="mt-3 text-base sm:text-lg text-fg-secondary leading-relaxed">
          {defs.subtitle}
        </p>
        <p data-testid="assessment-honest-label" className="mt-4 text-xs text-fg-muted">
          {defs.honest_label}
        </p>

        {error && (
          <Alert variant="destructive" className="mt-6" data-testid="assessment-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="assessment-progress-card">
          <div className="flex items-center justify-between">
            <p className="text-sm text-fg-secondary">Your progress</p>
            <p className="text-sm text-fg-primary font-medium tabular-nums">{answered} / {total}</p>
          </div>
          <div className="mt-3 h-2 bg-bg-base rounded-full overflow-hidden">
            <div className="h-full bg-progress-teal" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {defs.sections.map((s) => {
            const status = state.progress?.section_statuses?.[s.id] || "not_started";
            const map = { not_started: "Not started", in_progress: "In progress", complete: "Complete" };
            const firstQIdx = defs.questions.findIndex((q) => q.section === s.id);
            const inLane = firstQIdx >= 0;
            if (!inLane) return null;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onStart(firstQIdx)}
                data-testid={`assessment-section-${s.id}`}
                className="text-left rounded-lg border border-border-subtle bg-bg-surface p-4 hover:bg-bg-surface-hover transition-colors duration-200"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-fg-primary">
                    {s.index + 1}. {s.title}
                  </p>
                  <p className="text-xs text-fg-secondary">{map[status] || status}</p>
                </div>
                <p className="mt-1 text-xs text-fg-secondary">{s.description}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => onStart(resumeIdx)}
            data-testid="assessment-start-btn"
            className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
            {(answered > 0 || resumeIdx > 0) ? "Resume where I left off" : "Start the assessment"}
            <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
          </Button>
          <Link to={`/assessment/review${lane ? `?lane=${lane}` : ""}`}>
            <Button
              variant="outline"
              data-testid="assessment-review-link"
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <ListChecks className="h-4 w-4 mr-2" aria-hidden="true" />
              Review my answers
            </Button>
          </Link>
        </div>
        {submitted && (
          <p className="mt-6 text-sm text-fg-secondary" data-testid="assessment-submitted-notice">
            You've submitted this for analysis. Your results workspace is the next
            step in your journey.
          </p>
        )}
      </div>
    </AppShell>
  );
}


// ---------- Questions view ----------

const DRAFT_KEY_PREFIX = "cra3d.draft.";

function draftKeyFor(userId, qid) {
  return `${DRAFT_KEY_PREFIX}${userId || "anon"}.${qid}`;
}

function QuestionsView({ defs, state, cursorIdx, setCursorIdx, onStateChange, onExit, onReview }) {
  const questions = defs.questions;
  const q = questions[cursorIdx];
  const sec = defs.sections.find((s) => s.id === q.section);
  const { user } = useAuth();
  const [savedIndicator, setSavedIndicator] = useState("idle"); // idle | saving | saved | error
  const [lastFailedPayload, setLastFailedPayload] = useState(null); // {qid, value, skipped}
  const [confirmSkipNext, setConfirmSkipNext] = useState(false); // one-tap confirm for empty next
  const savingRef = useRef(0);
  const questionRef = useRef(null); // exposes flush()
  const navInflightRef = useRef(false);

  // Compute the effective saved answer for THIS question, preferring any
  // localStorage draft that a previous failed autosave left behind. This
  // means: if the server didn't accept the last save, the participant sees
  // their own typed text on the next render — never a blank field.
  const savedAnswer = state.answers[q.id];
  const effectiveAnswer = useMemo(() => {
    try {
      const raw = localStorage.getItem(draftKeyFor(user?.id, q.id));
      if (raw) {
        const parsed = JSON.parse(raw);
        // Only prefer if the saved-server value is missing OR the localStorage
        // draft is newer than the server updated_at (best-effort — server may
        // not have updated_at on brand-new answers).
        const serverTs = savedAnswer?.updated_at;
        if (!serverTs || Date.parse(serverTs) < parsed.ts) {
          return parsed.value;
        }
      }
    } catch (_) { /* localStorage unavailable — fall through */ }
    return savedAnswer?.answer;
    // We re-read localStorage on question change and on state.answers update
  }, [q.id, savedAnswer?.answer, savedAnswer?.updated_at, user?.id]);

  useEffect(() => { window.scrollTo(0, 0); }, [cursorIdx]);
  useEffect(() => { setConfirmSkipNext(false); }, [cursorIdx]);

  // Progress (across current lane's question set only)
  const answered = Object.keys(state.answers).filter(
    (qid) => !state.answers[qid].skipped && questions.some((qq) => qq.id === qid),
  ).length;
  const total = questions.length;
  const pct = Math.round(((cursorIdx + 1) / total) * 100);

  const performSave = useCallback(async (qid, value, skipped) => {
    return api.put("/assessment/answers", {
      answers: [{ question_id: qid, answer: value, skipped }],
    });
  }, []);

  const saveAnswer = useCallback(async (value, skipped) => {
    // Hardened saveAnswer: retries once with backoff on transient failure,
    // persists local draft to localStorage on failure so the participant's
    // typed text is NEVER lost, surfaces a Save-failed indicator and a
    // manual retry button when both attempts fail. Never re-throws, so the
    // fire-and-forget callers in AssessmentQuestion cannot leak an
    // unhandled promise rejection (the root cause the owner hit at Q37).
    const qid = q.id;
    const dkey = draftKeyFor(user?.id, qid);
    savingRef.current += 1;
    const mine = savingRef.current;
    setSavedIndicator("saving");
    // Persist the pending draft BEFORE we start — if the browser crashes
    // mid-request the draft survives.
    try {
      localStorage.setItem(dkey, JSON.stringify({ value, skipped: !!skipped, ts: Date.now() }));
    } catch (_) { /* localStorage unavailable — non-fatal */ }

    const attemptOnce = async () => {
      const { data } = await performSave(qid, value, skipped);
      return data;
    };

    try {
      const data = await attemptOnce();
      if (savingRef.current === mine) {
        onStateChange(data);
        setSavedIndicator("saved");
        setLastFailedPayload(null);
        try { localStorage.removeItem(dkey); } catch (_) {}
        setTimeout(() => {
          setSavedIndicator((s) => (s === "saved" ? "idle" : s));
        }, 1200);
      }
      return { ok: true };
    } catch (err1) {
      // First attempt failed. Wait 1.5s and try once more, unless a newer
      // save has already replaced us.
      if (savingRef.current !== mine) return { ok: false };
      await new Promise((r) => setTimeout(r, 1500));
      if (savingRef.current !== mine) return { ok: false };
      try {
        const data = await attemptOnce();
        if (savingRef.current === mine) {
          onStateChange(data);
          setSavedIndicator("saved");
          setLastFailedPayload(null);
          try { localStorage.removeItem(dkey); } catch (_) {}
          setTimeout(() => {
            setSavedIndicator((s) => (s === "saved" ? "idle" : s));
          }, 1200);
        }
        return { ok: true };
      } catch (err2) {
        if (savingRef.current === mine) {
          setSavedIndicator("error");
          setLastFailedPayload({ qid, value, skipped: !!skipped });
          // Do NOT auto-clear the error indicator. It stays visible until
          // the next successful save or an explicit retry.
        }
        return { ok: false, error: err2 };
      }
    }
  }, [q.id, user?.id, onStateChange, performSave]);

  const retryFailedSave = useCallback(async () => {
    if (!lastFailedPayload) return;
    await saveAnswer(lastFailedPayload.value, lastFailedPayload.skipped);
  }, [lastFailedPayload, saveAnswer]);

  const advance = useCallback(async (delta) => {
    if (navInflightRef.current) return; // duplicate-click guard
    navInflightRef.current = true;
    try {
      // Flush any pending text-input change BEFORE navigation.
      if (questionRef.current) {
        await questionRef.current.flush();
      }
      const next = Math.min(Math.max(cursorIdx + delta, 0), total - 1);
      const nextQ = questions[next];
      setCursorIdx(next);

      const nextSectionId = nextQ.section;
      const statuses = { ...(state.progress?.section_statuses || {}) };
      const answeredBySection = {};
      for (const qq of questions) {
        const a = state.answers[qq.id];
        if (a && !a.skipped && a.answer !== null && a.answer !== undefined && a.answer !== "") {
          answeredBySection[qq.section] = (answeredBySection[qq.section] || 0) + 1;
        }
      }
      for (const s of defs.sections) {
        const totalInSection = questions.filter((qq) => qq.section === s.id).length;
        if (totalInSection === 0) continue;
        if ((answeredBySection[s.id] || 0) >= totalInSection) statuses[s.id] = "complete";
        else if ((answeredBySection[s.id] || 0) > 0) statuses[s.id] = "in_progress";
      }
      statuses[nextSectionId] = statuses[nextSectionId] === "complete" ? "complete" : "in_progress";

      try {
        await api.put("/assessment/progress", {
          current_section: nextSectionId,
          current_question_id: nextQ.id,
          section_statuses: statuses,
        });
      } catch (_) { /* contained: progress is a hint, not a source of truth */ }
    } finally {
      navInflightRef.current = false;
    }
  }, [cursorIdx, total, questions, state.progress, state.answers, defs.sections, setCursorIdx]);

  // Detect empty non-skipped answer for the current question (used by
  // one-tap Next confirmation).
  const currentIsEmpty = useMemo(() => {
    const a = state.answers[q.id];
    if (a?.skipped) return false;
    const v = a?.answer;
    if (v === null || v === undefined) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
  }, [state.answers, q.id]);

  const handleNextClick = useCallback(async () => {
    // FAIL 1 fix: read the LIVE input value (including un-flushed text
    // typed within the 700 ms debounce window). The prior implementation
    // used `currentIsEmpty` which is derived from `state.answers`, and
    // that map only updates AFTER the debounce fires. A user who types
    // then immediately clicks Next would see the skip-confirmation modal
    // even though their text was visible in the input. Now we ask the
    // question component for its live value, and if the live value is
    // non-empty we flush the pending save and advance without prompting.
    const isSkipped = state.answers[q.id]?.skipped;
    const isEmptyLive = isSkipped
      ? false
      : (questionRef.current?.isEmptyLive?.() ?? currentIsEmpty);

    if (isEmptyLive && !confirmSkipNext) {
      setConfirmSkipNext(true);
      return;
    }
    setConfirmSkipNext(false);
    // Flush any pending debounced save BEFORE we advance, so the answer
    // is persisted before the next question mounts.
    try {
      await questionRef.current?.flush?.();
    } catch (_) { /* contained; save-failed indicator handles UI state */ }
    await advance(1);
  }, [currentIsEmpty, confirmSkipNext, advance, state.answers, q.id]);

  const isLast = cursorIdx === total - 1;

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-3">
          <p data-testid="questions-section-label" className="text-xs uppercase tracking-wider text-fg-secondary">
            Section {sec.index + 1} of {defs.sections.length} · {sec.title}
          </p>
          <p data-testid="questions-position" className="text-xs text-fg-secondary tabular-nums">
            Q {cursorIdx + 1} / {total}
          </p>
        </div>

        <div className="h-1.5 bg-bg-surface rounded-full overflow-hidden mb-6">
          <div
            data-testid="questions-progress-bar"
            className="h-full bg-accent-warm transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>

        <AssessmentQuestion
          ref={questionRef}
          question={q}
          answer={effectiveAnswer}
          skipped={!!savedAnswer?.skipped}
          onSave={(value, skipped) => saveAnswer(value, skipped)}
          onSkip={(shouldSkip) => saveAnswer(savedAnswer?.answer ?? null, shouldSkip)}
        />

        {savedIndicator === "error" && lastFailedPayload && (
          <div
            data-testid="save-failed-panel"
            className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-4"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-sm font-medium text-fg-primary">
                  Save failed — your draft is kept on this device
                </p>
                <p className="text-xs text-fg-secondary mt-1">
                  We couldn't reach the server just now. Your typed text is
                  saved in this browser and will be sent again on the next
                  save, or when you press retry.
                </p>
                <Button
                  size="sm"
                  onClick={retryFailedSave}
                  data-testid="save-failed-retry-btn"
                  className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                  Try to save again
                </Button>
              </div>
            </div>
          </div>
        )}

        {confirmSkipNext && (
          <div
            data-testid="confirm-empty-next-panel"
            className="mt-4 rounded-md border border-accent-warm/40 bg-accent-warm/5 p-4"
            role="status"
            aria-live="polite"
          >
            <p className="text-sm text-fg-primary">
              Leave this unanswered for now? You can return to it any time
              from the Review screen.
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                onClick={handleNextClick}
                data-testid="confirm-empty-next-yes"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                Yes, next
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmSkipNext(false)}
                data-testid="confirm-empty-next-cancel"
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => advance(-1)}
            disabled={cursorIdx === 0}
            data-testid="questions-back-btn"
            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back
          </Button>
          <span data-testid="autosave-indicator" className="text-xs text-fg-secondary" aria-live="polite">
            {savedIndicator === "saving" && "Saving…"}
            {savedIndicator === "saved" && "Saved"}
            {savedIndicator === "error" && "Save failed"}
            {savedIndicator === "idle" && `${answered} answered`}
          </span>
          {isLast ? (
            <Button
              onClick={async () => { if (questionRef.current) await questionRef.current.flush(); onReview(); }}
              data-testid="questions-review-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              Review my answers
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              onClick={handleNextClick}
              data-testid="questions-next-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={async () => { if (questionRef.current) await questionRef.current.flush(); onExit(); }}
            data-testid="questions-exit-btn"
            className="text-fg-secondary hover:text-fg-primary"
          >
            ← Save and exit to overview
          </button>
        </div>
      </div>
    </AppShell>
  );
}
